import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Heart, Building2, Home } from 'lucide-react';
import { createSession } from '../lib/supabaseService';

const ContextPage: React.FC = () => {
  const navigate = useNavigate();
  const [context, setContext] = useState('');
  const [topic, setTopic] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contexts = [
    { 
      id: 'couple', 
      label: 'Couple', 
      icon: Heart, 
      placeholder: 'Ex : Difficultés de communication dans le couple',
      bgColor: 'bg-pink-50',
      hoverBg: 'hover:bg-pink-50/50',
      iconColor: 'text-pink-500',
      selectedBg: 'bg-pink-100',
      ringColor: 'ring-pink-400'
    },
    { 
      id: 'amis', 
      label: 'Amis', 
      icon: Users, 
      placeholder: 'Ex : Malentendu ou conflit entre amis',
      bgColor: 'bg-purple-50',
      hoverBg: 'hover:bg-purple-50/50',
      iconColor: 'text-purple-500',
      selectedBg: 'bg-purple-100',
      ringColor: 'ring-purple-400'
    },
    { 
      id: 'associes', 
      label: 'Associés/Collègues', 
      icon: Building2, 
      placeholder: 'Ex : Tensions autour de la gestion d\'un projet',
      bgColor: 'bg-sky-50',
      hoverBg: 'hover:bg-sky-50/50',
      iconColor: 'text-sky-500',
      selectedBg: 'bg-sky-100',
      ringColor: 'ring-sky-400'
    },
    { 
      id: 'voisins', 
      label: 'Voisins', 
      icon: Home, 
      placeholder: 'Ex : Problème de bruit ou de respect des espaces communs',
      bgColor: 'bg-green-50',
      hoverBg: 'hover:bg-green-50/50',
      iconColor: 'text-green-500',
      selectedBg: 'bg-green-100',
      ringColor: 'ring-green-400'
    }
  ];

  const handleContextSelect = (selectedContext: string) => {
    setContext(selectedContext);
    const selectedContextData = contexts.find(c => c.id === selectedContext);
    if (selectedContextData) {
      setTopic('');
      const textarea = document.getElementById('topic') as HTMLTextAreaElement;
      if (textarea) {
        textarea.placeholder = selectedContextData.placeholder;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!context || !topic) {
      setError('Veuillez sélectionner un contexte et saisir un sujet');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { session_id } = await createSession(context, topic);
      navigate(`/session/${session_id}`);
    } catch (err) {
      setError('Une erreur est survenue lors de la création de la session');
      console.error('Error creating session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <h1 className="text-3xl font-light text-center mb-8 text-[#1A1A1A]">
        De quoi souhaitez-vous parler avec Léo ?
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {contexts.map(({ id, label, icon: Icon, bgColor, hoverBg, iconColor, selectedBg, ringColor }) => (
          <button
            key={id}
            onClick={() => handleContextSelect(id)}
            className={`card rounded-3xl flex flex-col items-center p-4 transition-all ${
              context === id
                ? `${selectedBg} ring-2 ${ringColor}`
                : `${bgColor} ${hoverBg}`
            }`}
          >
            <Icon
              size={32}
              className={context === id ? iconColor : 'text-slate-400'}
              strokeWidth={1.5}
            />
            <span className="mt-2 font-light">{label}</span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="topic" className="block text-sm font-medium text-[#1A1A1A] mb-2">
            Donnez un titre à votre discussion
          </label>
          <textarea
            id="topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Sélectionnez un contexte pour voir un exemple..."
            className="textarea-field rounded-3xl min-h-[80px]"
            rows={2}
          />
        </div>

        {error && (
          <div className="text-error text-sm p-3 bg-red-50 rounded-3xl">
            {error}
          </div>
        )}

        <div className="flex justify-center mt-8">
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary text-lg px-8 py-4 rounded-3xl flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 bg-gradient-to-r from-sky-500 to-sky-400 w-full md:w-auto min-w-[200px]"
          >
            {isLoading ? 'Création...' : 'Commencer la discussion'}
          </button>
        </div>
      </form>

      <p className="mt-8 text-center text-sm text-[#1A1A1A]">
        Vos propos resteront privés. Seul Léo verra ce que vous écrivez.
      </p>
    </div>
  );
};

export default ContextPage;